// CTenpaiPeek — 聽牌模式「瞇牌」動畫
// 聽牌後系統自動摸牌：在最右側摸進來的那張牌上播放瞇牌動畫。
//   - 牌的「後面」墊一張握牌手掌（peek-hand）
//   - 牌的「前面」蓋一根拇指（peek-thumb），以「圓心在圖片上方」為樞紐往左慢慢旋轉、
//     像掀開撲克牌一樣逐漸露出牌面（模擬瞇牌）
//   - 旋轉掀開後呼叫 onRevealed（caller 決定自摸胡 or 把這張打出去），peek 隨即淡出收場
//
// 自帶一張牌面 copy 疊在中層，避免去動 CHandView 內部 sprite 的 depth；
// 三層 depth：手掌(後) < 牌copy(中) < 拇指(前)，整體浮在所有 UI 之上。
//
// === 使用圖片 ===
// - public/assets/ui/peek-hand.png（key: PEEK_HAND_KEY）— 握牌手掌（牌後）
// - public/assets/ui/peek-thumb.png（key: PEEK_THUMB_KEY）— 拇指（牌前、旋轉掀牌）
// - 牌面 copy 透過 CTileSprite（face = HandBottom）間接使用 tile-blank-front + tiles-source

import type Phaser from 'phaser';

import type { TTile } from '@core/tile';
import { PEEK_HAND_KEY, PEEK_THUMB_KEY } from '@scenes/boot-scene';

import { CTileSprite, TileFace } from './tile-sprite';
import { killAndDestroy } from './tween-helpers';
import { UIDepth } from './ui-depths';

// ============================================================================
// 版面 / 動畫常數（瞇牌只用於本家底部手牌，tileScale 固定 ≈ 0.68，故用絕對縮放）
// ============================================================================
// 瞇牌三層由 UIDepth.TenpaiPeek 起往上疊（手掌 < 牌面 < 拇指）
const HAND_DEPTH = UIDepth.TenpaiPeek; // 手掌（牌後）
const TILE_DEPTH = UIDepth.TenpaiPeek + 1; // 牌面 copy（中層）
const THUMB_DEPTH = UIDepth.TenpaiPeek + 2; // 拇指（牌前）

const HAND_SCALE = 0.25; // 握牌手掌縮放（peek-hand 612×1116 → ≈104×190，約牌寬 1.7 倍，像手在握牌）
const HAND_ORIGIN_Y = 0.5; // 手掌錨點 Y（取手掌中心；翻轉後牌落在手指區，手身在牌下方）
const HAND_DX = 0; // 手掌相對牌心的水平偏移
const HAND_DY = 40; // 手掌相對牌心的垂直偏移（手身往下移 → 手指由下伸到牌、手腕在更下方朝玩家）

const THUMB_SCALE = 0.25; // 拇指縮放（peek-thumb 360×720 → ≈72×144，略大於牌身蓋住它、又像隻拇指）
const THUMB_PIVOT_ORIGIN_X = 0.3; // 拇指旋轉樞紐（origin）X：水平置中
const THUMB_PIVOT_ORIGIN_Y = 1; // 拇指旋轉樞紐 Y：圖片上方（圓心在上）
const THUMB_PIVOT_DX = 40; // 樞紐相對牌心的水平偏移
const THUMB_PIVOT_DY = 60; // 樞紐相對牌心的垂直偏移（負 = 往上；約牌上緣，本家牌身 144×0.68÷2 ≈ 49）
const THUMB_START_DEG = -50; // 起始角度：拇指再旋轉 90 度（對齊鏡射後的手掌）
const THUMB_REVEAL_DELTA_DEG = 45; // 掀牌旋轉量（從起始角往左旋開露出牌面；負 = 逆時針 / 往左）
const THUMB_END_DEG = THUMB_START_DEG + THUMB_REVEAL_DELTA_DEG; // 終止角度

// ---- 調校用：拇指旋轉圓心紅點（預覽 G 循環播放時顯示，方便對齊 THUMB_PIVOT_* 座標）----
const PIVOT_DOT_RADIUS = 6; // 紅點半徑
const PIVOT_DOT_COLOR = 0xff0000; // 紅
const PIVOT_DOT_DEPTH = THUMB_DEPTH + 1; // 高於拇指，確保看得到

const FADE_IN_MS = 150; // 手掌 / 拇指 / 牌面 淡入
const ROTATE_MS = 1800; // 拇指掀牌旋轉時長（慢）
const HOLD_AFTER_MS = 280; // 掀開後停留
const FADE_OUT_MS = 200; // 收場淡出

export type TTenpaiPeekOptions = {
  /** 摸牌（最右側分離牌）的畫面中心。 */
  readonly x: number;
  readonly y: number;
  /** 要瞇的那張牌（牌面 copy 用）。 */
  readonly tile: TTile;
  /** 牌的顯示縮放（= 該家手牌 scale）。 */
  readonly tileScale: number;
  /** 調校用：在拇指旋轉圓心畫紅點（預覽 G 循環播放時開啟），方便對齊 THUMB_PIVOT_* 座標。 */
  readonly debugPivot?: boolean;
};

/**
 * 聽牌瞇牌動畫。一次只跑一張牌：
 *   play(opts, onRevealed) → 淡入 → 拇指往左掀開 → onRevealed() → 淡出收場
 * onRevealed 內 caller 決定「自摸胡」或「把這張打出去」。
 */
export class CTenpaiPeek {
  private hand: Phaser.GameObjects.Image | null = null;
  private thumb: Phaser.GameObjects.Image | null = null;
  private tileCopy: CTileSprite | null = null;
  private pivotDot: Phaser.GameObjects.Arc | null = null; // 調校用圓心紅點

  constructor(private readonly scene: Phaser.Scene) {}

  /** 是否正在播放。 */
  isPlaying(): boolean {
    return this.hand !== null || this.thumb !== null || this.tileCopy !== null;
  }

  /**
   * 在 (opts.x, opts.y) 播放瞇牌動畫。
   * @param onRevealed 拇指掀開、牌面露出後觸發（caller 決定胡 / 打出）；之後 peek 自動淡出
   */
  play(opts: TTenpaiPeekOptions, onRevealed: () => void): void {
    this.destroy(); // 防衛：清掉上一輪殘留

    const { x, y, tile, tileScale } = opts;

    // 1. 手掌（牌後）— 原圖已處理好朝向（手指朝上、手腕在下），手身落在牌下方、由下往上握住牌
    const hand = this.scene.add
      .image(x + HAND_DX, y + HAND_DY, PEEK_HAND_KEY)
      .setOrigin(0.5, HAND_ORIGIN_Y)
      .setScale(HAND_SCALE)
      .setDepth(HAND_DEPTH)
      .setAlpha(0);
    this.hand = hand;

    // 2. 牌面 copy（中層）
    const tileCopy = new CTileSprite(this.scene, x, y, tile, TileFace.HandBottom);
    tileCopy.setScale(tileScale);
    tileCopy.setDepth(TILE_DEPTH);
    tileCopy.setAlpha(0);
    this.tileCopy = tileCopy;

    // 3. 拇指（牌前，繞圓心旋轉）— 原圖已處理好朝向，對齊手掌
    const thumb = this.scene.add
      .image(x + THUMB_PIVOT_DX, y + THUMB_PIVOT_DY, PEEK_THUMB_KEY)
      .setOrigin(THUMB_PIVOT_ORIGIN_X, THUMB_PIVOT_ORIGIN_Y)
      .setScale(THUMB_SCALE)
      .setDepth(THUMB_DEPTH)
      .setAngle(THUMB_START_DEG)
      .setAlpha(0);
    this.thumb = thumb;

    // 調校用：在拇指旋轉圓心畫紅點（圓心 = 拇指 image 的擺放點，旋轉不會移動它）
    if (opts.debugPivot) {
      this.pivotDot = this.scene.add
        .circle(x + THUMB_PIVOT_DX, y + THUMB_PIVOT_DY, PIVOT_DOT_RADIUS, PIVOT_DOT_COLOR)
        .setDepth(PIVOT_DOT_DEPTH);
    }

    // 淡入 → 旋轉掀開 → onRevealed → 淡出
    this.scene.tweens.add({
      targets: [hand, thumb, tileCopy],
      alpha: 1,
      duration: FADE_IN_MS,
      onComplete: () => {
        this.scene.tweens.add({
          targets: thumb,
          angle: THUMB_END_DEG,
          duration: ROTATE_MS,
          ease: 'Sine.easeInOut',
          onComplete: () => {
            onRevealed();
            this.scene.time.delayedCall(HOLD_AFTER_MS, () => this.fadeOutAndDestroy());
          },
        });
      },
    });
  }

  /** 淡出三層並銷毀。 */
  private fadeOutAndDestroy(): void {
    const targets = [this.hand, this.thumb, this.tileCopy].filter(
      Boolean
    ) as Phaser.GameObjects.GameObject[];
    if (targets.length === 0) return;
    this.scene.tweens.add({
      targets,
      alpha: 0,
      duration: FADE_OUT_MS,
      onComplete: () => this.destroy(),
    });
  }

  /** 立即銷毀（回合結束 / 中斷）。 */
  destroy(): void {
    killAndDestroy(this.scene, this.hand, this.thumb, this.tileCopy, this.pivotDot);
    this.hand = null;
    this.thumb = null;
    this.tileCopy = null;
    this.pivotDot = null;
  }
}
